import React from 'react';
import { CreditGroupDto, CreditGroupItemDto } from '../../../../modelView';
import { useTranslation } from 'react-i18next';
import {
  isValidMonthlyInstalment,
  maxMonthlyInstalment,
  minMonthlyInstalment,
} from '../../../../modelView/credit/CreditUtil';
import { formatPercentageFromDecimal } from '../../../../utils/formatterHelper';
import {
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from '@mui/material';
import { AddOutlined, DeleteOutlined } from '@mui/icons-material';
import OptionsSelect from '../../../financeCalculation/optionsSelect';

export default function CreditGroupTable(props: {
  creditGroup: CreditGroupDto;
  handleUpdate: (creditGroup: CreditGroupDto) => void;
  monthOptions?: Array<number>;
  advanceOptions?: Array<number>;
}) {
  const { t } = useTranslation();

  const removeRowTooltip = t('actions.remove');
  const addRowTooltip = t('actions.add');

  const handleUpdate = (creditGroup: CreditGroupDto) => {
    props.handleUpdate(creditGroup);
  };

  const removeRow = (index: number) => {
    const newItems = props.creditGroup.items.filter((item, idx) => idx !== index);
    handleUpdate(new CreditGroupDto(props.creditGroup.type, newItems, props.creditGroup.creditCalculationParams));
  };

  const addRow = () => {
    const newItems = [...props.creditGroup.items, new CreditGroupItemDto(null, null, null)];
    handleUpdate(new CreditGroupDto(props.creditGroup.type, newItems, props.creditGroup.creditCalculationParams));
  };

  const handleItemUpdate = (newItem: CreditGroupItemDto, index: number) => {
    const newItems = [...props.creditGroup.items];
    newItems[index] = newItem;
    handleUpdate(new CreditGroupDto(props.creditGroup.type, newItems, props.creditGroup.creditCalculationParams));
  };

  const updateMonth = (item: CreditGroupItemDto, index: number, newValue: string | number | null) => {
    const updatedValue: number | null =
      newValue != undefined ? (typeof newValue === 'string' ? parseInt(newValue) : newValue) : null;
    const updatedData: CreditGroupItemDto = new CreditGroupItemDto(
      updatedValue,
      item.advancedPayment,
      item.monthlyInstalment,
    );
    handleItemUpdate(updatedData, index);
  };

  const updateAdvancedPayment = (item: CreditGroupItemDto, index: number, newValue: string | number | null) => {
    const updatedValue: number | null =
      newValue != undefined ? (typeof newValue === 'string' ? parseInt(newValue) / 100 : newValue) : null;
    const updatedData: CreditGroupItemDto = new CreditGroupItemDto(item.month, updatedValue, item.monthlyInstalment);
    handleItemUpdate(updatedData, index);
  };

  const updateMonthlyInstalment = (item: CreditGroupItemDto, index: number, newValue: string) => {
    const updatedValue: number | null = newValue ? parseInt(newValue) : null;
    const updatedData: CreditGroupItemDto = new CreditGroupItemDto(item.month, item.advancedPayment, updatedValue);
    handleItemUpdate(updatedData, index);
  };

  const renderMonthOptionsSelect = (item: CreditGroupItemDto, index: number) => {
    return (
      <OptionsSelect
        item={item}
        index={index}
        value={item.month}
        // @ts-ignore
        onChangeHandler={updateMonth}
        options={props.monthOptions}
        fieldKey={'month'}
      />
    );
  };

  const renderAdvanceOptionsSelect = (item: CreditGroupItemDto, index: number) => {
    return (
      <OptionsSelect
        item={item}
        index={index}
        value={item.advancedPayment ? item.advancedPayment * 100 : 0}
        // @ts-ignore
        onChangeHandler={(item, index, newValue) => updateAdvancedPayment(item, index, newValue / 100)}
        options={props.advanceOptions}
        fieldKey={'advance'}
      />
    );
  };

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell align='center'>{t('pages.vehicleCards.detail.finance.credit.creditGroupTable.month')}</TableCell>
            <TableCell align='center'>
              {t('pages.vehicleCards.detail.finance.credit.creditGroupTable.advancedPayment')}
            </TableCell>
            <TableCell align='center'>
              {t('pages.vehicleCards.detail.finance.credit.creditGroupTable.monthlyInstalment')}
            </TableCell>

            <TableCell align='center'>
              <Tooltip title={addRowTooltip}>
                <IconButton onClick={addRow}>
                  <AddOutlined />
                </IconButton>
              </Tooltip>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {props.creditGroup.items.map((item, index) => (
            <TableRow key={'credit-group-item-row' + index}>
              <TableCell align='center'>
                {props.monthOptions ? (
                  renderMonthOptionsSelect(item, index)
                ) : (
                  <TextField
                    error={!item.month}
                    value={item.month}
                    onChange={(event) => updateMonth(item, index, event.target.value)}
                    type={'text'}
                  />
                )}
              </TableCell>
              <TableCell align='center'>
                {props.advanceOptions ? (
                  renderAdvanceOptionsSelect(item, index)
                ) : (
                  <TextField
                    error={typeof item.advancedPayment !== 'number'}
                    value={formatPercentageFromDecimal(item.advancedPayment)}
                    onChange={(event) => updateAdvancedPayment(item, index, event.target.value)}
                    type={'text'}
                  />
                )}
              </TableCell>
              <TableCell align='center'>
                <TextField
                  className={'w-100'}
                  error={!isValidMonthlyInstalment(item.monthlyInstalment, true)}
                  value={item.monthlyInstalment}
                  onChange={(event) => updateMonthlyInstalment(item, index, event.target.value)}
                  type={'number'}
                  InputProps={{ inputProps: { min: minMonthlyInstalment, max: maxMonthlyInstalment } }}
                />
              </TableCell>
              <TableCell>
                <Tooltip title={removeRowTooltip}>
                  <IconButton onClick={() => removeRow(index)}>
                    <DeleteOutlined />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
